import type { NoteList } from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';

import type {
  NoteEditorBlock,
  NoteEditorContent,
  NoteEditorLine,
  NoteEditorTextTone,
} from '@/entities/templates';

import {
  listNoteLineOpeners,
  listNoteOptionLines,
  listShownLineOpeners,
  type NamedNoteLinkOpener,
  type NoteLinkOpener,
  type NoteLinkSections,
} from './blockEditorView';
import { nameNoteLinkOpener } from './blockTemplate';
import { listNoteChoiceText } from './noteChoiceText';
import {
  isRemovedNoteWording,
  isUnconfirmedAiValue,
  listNoteLineResetParts,
  startsWithPunctuation,
  type NoteLineResetParts,
  type NoteSurface,
  type SurfaceBlock,
  type SurfaceLine,
} from './noteSurface';
import { findNamedNoteValue, stripInlineGuidance } from './noteView';

// What the widget of a block reads in the document editor.
export type NoteBlockEntry = {
  block: SurfaceBlock;
  lineId: string;
  // The line's text right after the block starts with a period.
  periodAfter: boolean;
  // The line's wording says the field's name, which the empty field then
  // leaves to it.
  nameInLine: boolean;
};

const startsWithPeriod = (block: SurfaceBlock | undefined) =>
  block?.def?.Kind.case === 'Text' && block.text.trimStart().startsWith('.');

// The line's text right after its block at `index` starts with a period.
export const isNotePeriodAfter = (line: SurfaceLine, index: number) =>
  startsWithPeriod(line.blocks[index + 1]);

const comparable = (text: string) =>
  text.toLowerCase().replace(/\s+/g, '').replace(/:+$/, '');

const readFieldLabel = (block: SurfaceBlock) =>
  (block.def?.Kind.value as { Label?: string } | undefined)?.Label?.trim();

// Whether the line's wording says the field's label already, ignoring case,
// spacing and a closing colon: an empty field shows its label, unless it
// does, and what it takes in a word of its own then.
export const isNoteFieldNamedByLine = (
  line: SurfaceLine,
  block: SurfaceBlock,
) => {
  const label = readFieldLabel(block);
  const wording = line.blocks
    .filter((item) => item.def?.Kind.case === 'Text')
    .map((item) => item.text)
    .join(' ');
  return !!label && comparable(wording).includes(comparable(label));
};

// What a line's widgets read in the document editor: its lead and tail, or
// the widget of a line shown as one. It holds no value, so a new value
// never renders the line's widgets again.
export type NoteLineEntry = {
  addedByAi: boolean;
  // Revealed since the note was first shown.
  isNew: boolean;
  // A partner template's line printed without its tooth.
  teethMissing: boolean;
  // What a line AI removed printed; empty on every other line.
  text: string;
  // The options its mark names (`listShownLineOpeners`), and the shown lines
  // its options open.
  openers: NamedNoteLinkOpener[];
  opens: string[];
};

type BlockScope = { editable: boolean; listening: boolean };

// The marks and actions a block shows around its value: a partner
// template's default, what AI added, a value AI assumed or suggested to
// confirm with the words it was read from, and a later AI reading.
export const listNoteBlockMarks = (
  block: SurfaceBlock,
  { editable }: Pick<BlockScope, 'editable'>,
) => ({
  partnerDefault: !!block.partnerDefault && !block.pending,
  addedByAi: block.addedByAi,
  confirm: editable && isUnconfirmedAiValue(block),
  quote:
    (isUnconfirmedAiValue(block) || !!block.fromInstruction) && !!block.quote,
  correction: block.correction !== undefined,
});

const isAiEdited = (block: SurfaceBlock) => block.spans.some((span) => span.ai);

// What a text block shows after its text: the way back to the template
// wording (a removed one shows nothing, its line's reset brings it back), or
// a free text's AI basis and the way back to the AI value, and the marks
// every block shows.
export const listTextBlockAside = (
  block: SurfaceBlock,
  { editable, listening }: BlockScope,
) => {
  const freeText = block.def?.Kind.case === 'FreeText';
  return {
    restoreWording:
      !freeText &&
      editable &&
      (!!block.source || isAiEdited(block)) &&
      !block.addedByAi &&
      !isRemovedNoteWording(block),
    aiBasis: freeText && editable && block.source === 'ai' && block.resolved,
    valueTools:
      freeText &&
      editable &&
      block.source === 'manual' &&
      (block.canRevert || listening),
    ...listNoteBlockMarks(block, { editable }),
  };
};

const wordingTone = (block: SurfaceBlock): NoteEditorTextTone => {
  if (block.addedByAi) {
    return 'aiAdded';
  }
  if (block.source === 'manual') {
    return 'edited';
  }
  return block.source === 'ai' && !isAiEdited(block) ? 'ai' : null;
};

const freeTextTone = (block: SurfaceBlock): NoteEditorTextTone => {
  if (block.source !== 'ai' || !block.resolved) {
    return null;
  }
  return block.basis === 'assumed' ? 'assumed' : 'ai';
};

// The words the editor shows for its text, in the interface language.
export type NoteEditorTexts = {
  freeTextPlaceholder: string;
  lineNotePlaceholder: string;
  // Under the title of a section that shows no line, if anything.
  emptySectionText: string;
  // What an AI value rests on, if it says more than the value.
  describeAiBasis: (block: SurfaceBlock) => string | undefined;
};

// A choice prints its chosen values as text of the document, to type over,
// when the note may change and the values print as text rather than chips.
// The template's language prints an explicit none.
export type NoteChoiceTextScope = { lists: NoteList[]; language?: string };

const toEditorBlocks = ({
  block,
  nameInLine,
  spaceBefore,
  scope,
  texts,
  choices,
}: {
  block: SurfaceBlock;
  nameInLine: boolean;
  spaceBefore: boolean;
  scope: BlockScope;
  texts: NoteEditorTexts;
  choices: NoteChoiceTextScope | null;
}): NoteEditorBlock[] => {
  const { id, def } = block;
  const aside: NoteEditorBlock[] = Object.values(
    listTextBlockAside(block, scope),
  ).some(Boolean)
    ? [{ kind: 'aside', id }]
    : [];
  switch (def?.Kind.case) {
    case 'Text':
      return [
        {
          kind: 'wording',
          id,
          runs: block.spans.length
            ? block.spans.map((span) => ({
                text: stripInlineGuidance(span.text),
                marked: span.ai,
              }))
            : [{ text: stripInlineGuidance(block.text), marked: false }],
          tone: wordingTone(block),
          spaceBefore,
        },
        ...aside,
      ];
    case 'FreeText': {
      const tone = freeTextTone(block);
      return [
        {
          kind: 'freeText',
          id,
          text: block.selection.other || (block.resolved ? block.text : ''),
          placeholder:
            (!nameInLine && readFieldLabel(block)) || texts.freeTextPlaceholder,
          tone,
          hint: (tone && texts.describeAiBasis(block)) ?? '',
          spaceBefore,
        },
        ...aside,
      ];
    }
    case 'Choice':
    case 'MultiChoice':
    case 'ListRef': {
      const values =
        choices && scope.editable
          ? listNoteChoiceText(block, def, choices.lists, choices.language)
          : [];
      if (!values.length) {
        return [{ kind: 'widget', id, def, spaceBefore }];
      }
      return [
        { kind: 'choice', id, values, tone: freeTextTone(block), spaceBefore },
        { kind: 'aside', id },
      ];
    }
    default:
      return [{ kind: 'widget', id, def, spaceBefore }];
  }
};

const toEditorLine = ({
  line,
  scope,
  texts,
  choices,
}: {
  line: SurfaceLine;
  scope: BlockScope;
  texts: NoteEditorTexts;
  choices: NoteChoiceTextScope | null;
}): NoteEditorLine =>
  line.state === 'aiRemoved'
    ? { kind: 'widget', id: line.id }
    : {
        kind: 'blocks',
        id: line.id,
        blocks: line.blocks.flatMap((block, index) =>
          toEditorBlocks({
            block,
            nameInLine: isNoteFieldNamedByLine(line, block),
            spaceBefore: index > 0 && !startsWithPunctuation(block),
            scope,
            texts,
            choices,
          }),
        ),
        note: line.note
          ? { text: line.note, placeholder: texts.lineNotePlaceholder }
          : null,
        status: line.state,
        subheading: !!line.def?.Subheading,
      };

// The note's surface as the document editor holds it: its sections and the
// lines they show, wording, free text, line notes and chosen values as
// text, every other block as a widget, and a line AI removed as a widget of
// its own.
export const buildNoteEditorContent = ({
  surface,
  scope,
  texts,
  choices = null,
}: {
  surface: NoteSurface;
  scope: BlockScope;
  texts: NoteEditorTexts;
  choices?: NoteChoiceTextScope | null;
}): NoteEditorContent => ({
  sections: surface.sections.map((section) => ({
    id: section.id,
    code: section.code,
    title: section.title,
    // A section the clinician can type a line into holds an empty line
    // instead.
    emptyText:
      section.lines.length || scope.editable ? '' : texts.emptySectionText,
    canAddLine: scope.editable,
    noteLineIds: section.noteLineIds,
    lines: section.lines.map((line) =>
      toEditorLine({ line, scope, texts, choices }),
    ),
  })),
});

export const listNoteBlockEntries = (
  surface: NoteSurface,
): Record<string, NoteBlockEntry> =>
  Object.fromEntries(
    surface.sections.flatMap((section) =>
      section.lines.flatMap((line) =>
        line.blocks.map((block, index) => [
          block.id,
          {
            block,
            lineId: line.id,
            periodAfter: isNotePeriodAfter(line, index),
            nameInLine: isNoteFieldNamedByLine(line, block),
          },
        ]),
      ),
    ),
  );

// The links the note shows are those between the lines it shows.
const toNoteLinkSections = (surface: NoteSurface): NoteLinkSections => ({
  sections: surface.sections.map((section) => ({
    Title: section.title,
    Lines: section.lines.flatMap((line) => (line.def ? [line.def] : [])),
  })),
});

const listLineOpens = (sections: NoteLinkSections, line: SurfaceLine) =>
  (line.def?.Blocks ?? [])
    .flatMap((block) =>
      block.Kind.case === 'Choice' || block.Kind.case === 'MultiChoice'
        ? block.Kind.value.Values.flatMap((value) =>
            listNoteOptionLines(sections, block.ID, value),
          )
        : [],
    )
    .filter((lineId, index, all) => all.indexOf(lineId) === index);

export const listNoteLineEntries = (
  surface: NoteSurface,
  isNew: (line: SurfaceLine) => boolean,
): Record<string, NoteLineEntry> => {
  const sections = toNoteLinkSections(surface);
  const blocks = new Map(
    surface.sections.flatMap((section) =>
      section.lines.flatMap((line) =>
        line.blocks.map((block): [string, SurfaceBlock] => [block.id, block]),
      ),
    ),
  );
  const isChosen = ({ blockId, value }: NoteLinkOpener) =>
    !!findNamedNoteValue(blocks.get(blockId)?.selection.values ?? [], value);
  return Object.fromEntries(
    surface.sections.flatMap((section) =>
      section.lines.map((line): [string, NoteLineEntry] => [
        line.id,
        {
          addedByAi: line.addedByAi,
          isNew: isNew(line),
          teethMissing:
            line.teethMissing && !line.blocks.some((block) => block.pending),
          text: line.state === 'aiRemoved' ? line.text : '',
          openers: listShownLineOpeners(
            listNoteLineOpeners(sections, line.id).map((opener) => ({
              ...opener,
              label: nameNoteLinkOpener(sections, opener),
            })),
            isChosen,
          ),
          opens: listLineOpens(sections, line),
        },
      ]),
    ),
  );
};

// What each line's reset takes back, by line id.
export const listNoteLineResets = (
  surface: NoteSurface,
): Record<string, NoteLineResetParts> =>
  Object.fromEntries(
    surface.sections.flatMap((section) =>
      section.lines.map((line) => [line.id, listNoteLineResetParts(line)]),
    ),
  );
